import React from 'react';
import styled from 'styled-components';
import { } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #004744;
  font-style: italic;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #004744;
  }
`;

const Network: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
        </svg>
      ),
      title: language === 'it' ? 'GESTIONE COMPLETA' : 'COMPLETE MANAGEMENT',
      description: language === 'it' 
        ? 'Risparmia tempo, ci occupiamo di tutto: annunci, accoglienza degli ospiti, obblighi, pulizie...'
        : 'Save time, we take care of everything: announcements, welcoming guests, obligations, cleaning...'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      title: language === 'it' ? 'RIDUZIONE DEI RISCHI E DEI COSTI DI ACQUISIZIONE' : 'REDUCTION OF RISKS AND ACQUISITION COSTS',
      description: language === 'it' 
        ? 'Entra nel business degli affitti a breve termine con tranquillità sapendo che non dovrai affrontare l\'onere finanziario e il rischio associati alla pubblicità tradizionale.'
        : 'Get into the short-term rental business with peace of mind knowing you won\'t have to deal with the financial burden and risk associated with traditional advertising.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      ),
      title: language === 'it' ? 'CREDIBILITÀ E AFFIDABILITÀ' : 'CREDIBILITY AND RELIABILITY',
      description: language === 'it' 
        ? 'Collaborare con affiliati fidati e rispettati nel settore può migliorare la credibilità del tuo marchio. Gli affiliati che raccomandano i tuoi servizi possono costruire fiducia con potenziali clienti.'
        : 'Partnering with trusted and respected affiliates in the industry can enhance your brand credibility. Affiliates who recommend your services can build trust with potential customers.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
        </svg>
      ),
      title: language === 'it' ? 'FLESSIBILITÀ E SCALABILITÀ' : 'FLEXIBILITY AND SCALABILITY',
      description: language === 'it' 
        ? 'Il modello di affiliazione multilivello è flessibile e scalabile. Puoi espandere la tua rete di affiliati man mano che la tua attività cresce e le esigenze cambiano, adattando le partnership in base alle prestazioni e alle opportunità di mercato.'
        : 'The multi-tiered affiliate model is flexible and scalable. You can expand your affiliate network as your business grows and needs change, tailoring partnerships based on performance and market opportunities.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Network | J-O-H' : 'Network | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Il nostro network di professionisti del settore immobiliare a breve termine' 
            : 'Our network of short-term real estate professionals'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'NETWORK' : 'NETWORK'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Il nostro network comprende professionisti del settore immobiliare specializzati in affitti brevi, che collaborano per offrire un servizio completo e di qualità.'
              : 'Our network includes short-term real estate professionals who collaborate to offer a complete and quality service.'}
          </Description>
          
          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard key={index}>
                <IconContainer>
                  {benefit.icon}
                </IconContainer>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitCard>
            ))}
          </BenefitsGrid>
          
          <SectionTitle>
            {language === 'it' ? 'AFFILIAZIONE A LIVELLI MULTIPLI' : 'AFFILIATION AT MULTIPLE LEVELS'}
          </SectionTitle>
          
          <Description>
            {language === 'it' 
              ? 'Ecco come funziona: in un programma di affiliazione a più livelli, ci sono diversi livelli di partecipazione o reclutamento. Il programma è strutturato gerarchicamente, con affiliati a livelli più alti che guadagnano commissioni da quelli a livelli inferiori. Gli affiliati guadagnano commissioni o bonus non solo dalle loro vendite o azioni dirette, ma anche dalle vendite o azioni generate dai loro affiliati reclutati.'
              : 'Here\'s how it works: in a tiered affiliate program, there are different levels of participation or recruitment. The program is structured hierarchically, with affiliates at higher levels earning commissions from those at lower levels. Affiliates earn commissions or bonuses not only from their direct sales or actions, but also from sales or actions generated by their recruited affiliates.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Network; 